/* Nethrasap frontend API client.
 *
 * Plain fetch wrapper for the FastAPI backend.
 * Base URL priority:
 *   1. runtime override: `window.NETHRA_API_BASE`
 *   2. build-time env:   `import.meta.env.VITE_API_BASE`
 *   3. same-origin fallback (works when frontend + backend share a host)
 *
 * If VITE_API_BASE already contains `/api/v1`, we don't double-suffix.
 *
 *   await api.products.list({ q: "amox", category: "antibiotics" })
 *   await api.auth.login({ email, password })
 *   await api.auth.me()
 */
const API_PREFIX = "/api/v1";
const TOKEN_KEY = "nethrasap.token";

const ENV_BASE = (import.meta.env.VITE_API_BASE || "").replace(/\/$/, "");

function getBaseUrl() {
  if (typeof window === "undefined") return ENV_BASE;
  return ((window.NETHRA_API_BASE || ENV_BASE) || "").replace(/\/$/, "");
}

function buildUrl(path, query) {
  const base = getBaseUrl();
  // If the configured base already ends with /api/v1, don't append it again.
  const hasPrefix = base.endsWith(API_PREFIX);
  const origin = base || (typeof window !== "undefined" ? window.location.origin : "");
  const prefix = hasPrefix ? "" : API_PREFIX;
  const url = new URL(origin + prefix + path);
  if (query && typeof query === "object") {
    Object.entries(query).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "" && v !== false) {
        url.searchParams.set(k, v);
      }
    });
  }
  return url.toString();
}

function readToken() {
  if (typeof window === "undefined") return null;
  if (window.NETHRA_TOKEN) return window.NETHRA_TOKEN;
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch (_) {
    return null;
  }
}

export function setToken(token) {
  if (typeof window === "undefined") return;
  if (token) {
    window.NETHRA_TOKEN = token;
    try { window.localStorage.setItem(TOKEN_KEY, token); } catch (_) {}
  } else {
    delete window.NETHRA_TOKEN;
    try { window.localStorage.removeItem(TOKEN_KEY); } catch (_) {}
  }
}

function safeJson(text) {
  try { return JSON.parse(text); } catch (_) { return text; }
}

async function request(method, path, { query, body, headers } = {}) {
  const token = readToken();
  const res = await fetch(buildUrl(path, query), {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: "Bearer " + token } : {}),
      ...(headers || {}),
    },
    body: body ? JSON.stringify(body) : undefined,
    credentials: "same-origin",
  });
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) {
    const err = new Error((data && data.detail) || res.statusText || "Request failed");
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export const api = {
  get base() { return getBaseUrl(); },

  health: () => request("GET", "/health"),

  products: {
    list: (query) => request("GET", "/products", { query }),
    get: (slug) => request("GET", "/products/" + encodeURIComponent(slug)),
    reviews: (slug, query) =>
      request("GET", "/products/" + encodeURIComponent(slug) + "/reviews", { query }),
    postReview: (slug, body) =>
      request("POST", "/products/" + encodeURIComponent(slug) + "/reviews", { body }),
  },

  categories: {
    list: () => request("GET", "/categories"),
    get: (slug) => request("GET", "/categories/" + encodeURIComponent(slug)),
  },

  auth: {
    signup: (body) => request("POST", "/auth/signup", { body }),
    login: (body) => request("POST", "/auth/login", { body }),
    refresh: (refresh_token) => request("POST", "/auth/refresh", { body: { refresh_token } }),
    logout: (refresh_token) => request("POST", "/auth/logout", { body: { refresh_token } }),
    me: () => request("GET", "/auth/me"),
  },
};

export { request, readToken };

if (typeof window !== "undefined") {
  // Keep a window-global handle for non-React callers (legacy code, console).
  window.api = api;
}
